<template>
  <div class="product-item card">
    <div class="card-body">
      <div class="row p-2">
        <div class="col-2 d-none d-md-block">
          <i class="bi bi-image dummy-productimage"></i>
        </div>
        <div class="col-10">
          <div class="row">
            <div class="col-md-8">
              <h5 class="bold mb-0">
                {{
                  itemData.product
                    ? getValue(itemData.product.customerProductName)
                    : ""
                }}
              </h5>

              <div>
                {{
                  itemData.product
                    ? getValue(itemData.product.customerProductNumber)
                    : ""
                }}
                {{ itemData.product.name }}
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="pe-5 d-inline-block">
                    {{ itemData.product.productPackagingName }}
                  </div>
                  <div class="pe-5 d-inline-block">
                    MOQ:
                    {{
                      itemData.product
                        ? formatNumberWithSpaces(getValue(itemData.product.moq))
                        : ""
                    }}
                    boxes
                    <i class="bi bi-info-circle" v-tooltip="moqInfoBox"></i>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4 text-end">
              <div class="row">
                <div class="col-md-6 text-start">
                  <div v-if="lineDiscountSum > 0" class="discount-box">
                    Discount: -{{ totalDiscount }}
                  </div>
                </div>
                <div class="col-md-6">
                  <h5 class="bold">
                    {{ totalPrice }}
                  </h5>
                  <div class="d-flex flex-grow-1 justify-content-end text-end">
                    {{
                      currentPrice
                        ? formatCurrency(
                            getValue(currentPrice),
                            getValue(itemData.product.currency)
                          )
                        : currentPriceInfo
                        ? formatCurrency(
                            getValue(currentPriceInfo.price),
                            getValue(itemData.product.currency)
                          )
                        : ""
                    }}
                    / box

                    <br />
                    <template v-if="itemData.discount"
                      >Discount: -{{
                        formatNumber(getValue(itemData.discount)) + "%"
                      }}</template
                    >
                  </div>
                </div>
              </div>
            </div>

            <div class="row py-3">
              <div class="col-md-12">
                {{
                  itemData.product ? getValue(itemData.product.description) : ""
                }}
              </div>
            </div>
          </div>

          <!-- Artwork version -->
          <div class="row mb-1">
            <div class="col-md-2 pt-2">Artwork version</div>
            <div
              class="col-md-2"
              v-for="artWork in ArtworkTypesconfig"
              :key="artWork.key"
              :style="{
                display: artworkSelections[artWork.key]?.id ? 'block' : 'none',
              }"
            >
              <VMenu>
                <div class="input-group mb-3">
                  <span class="input-group-text" :title="artWork.title"
                    ><i :class="artWork.icon" :title="artWork.title"></i
                  ></span>
                  <select class="form-control" disabled>
                    <option :value="artworkSelections[artWork.key]?.id">
                      {{
                        artworkSelections[artWork.key]?.name ||
                        "No name available"
                      }}
                    </option>
                  </select>
                </div>
                <template #popper>{{ artWork.tooltip }}</template>
              </VMenu>
            </div>
          </div>

          <!-- Comments etc -->
          <div class="row mb-3">
            <div class="col-md-2 pt-2">Comment</div>
            <div class="col-md-10">
              <textarea
                class="form-control"
                v-model="artworkComment"
                rows="1"
              ></textarea>
            </div>
          </div>

          <div class="row">
            <div class="col-md-2 pt-2">Requested Delivery</div>
            <div class="col-md-3">
              <Datepicker v-model="selectedDate" :format="dateFormat" />

              <div class="small red p-2" v-if="selectedDate === null">
                Date is required
              </div>
            </div>
            <div class="col-md-1">
              <i
                class="bi bi-info-circle fs-3 d-inline-block float-end"
                v-tooltip="{
                  content: priceInfoBox,
                }"
              ></i>
            </div>
            <div class="col-md-2">
              <input
                type="number"
                class="form-control"
                id="customQuantity"
                :min="itemData.product.moq"
                v-model="customQuantity"
                placeholder="Enter quantity..."
                v-on:keyup="customQuantityChange()"
              />
              <div class="small red p-2" v-if="showNotInRange">
                Quantity is not in quantity range
              </div>
              <div
                class="small red p-2"
                v-if="
                  customQuantity &&
                  itemData.product.moq &&
                  itemData.product.moq - 1 >= customQuantity
                "
              >
                Min. order quantity is {{ itemData.product.moq }}
              </div>
            </div>

            <div class="col-md-3">
              <button
                type="button"
                class="btn btn-primary w-100"
                :disabled="
                  showNotInRange ||
                  (customQuantity !== undefined && customQuantity === 0) ||
                  selectedDate === undefined ||
                  selectedDate === null
                "
                v-on:click="updateCart()"
              >
                Update
              </button>
            </div>
            <div class="col-md-1">
              <i
                class="bi bi-x-lg fs-3 hand"
                v-on:click="removeProduct()"
                title="Remove product"
              ></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-facing-decorator";
import { useCartStore } from "@/store/cartStore";
import { toast } from "vue3-toastify";
import { PriceInfo, ProductInformation } from "@/data/client";
import { CartItem } from "@/interface/CartItem";
import Datepicker from "@vuepic/vue-datepicker";
import moment from "moment";
import { formatCurrency } from "@/helpers/HelperTool";
import { SelectedArtWork } from "@/interface/SelectedArtWork";
import { artworkTypeConfig } from "@/interface/ArtWorkTypes";
import {
  findPriceRange,
  getEffectiveDiscountPercent,
} from "@/helpers/pricingHelpers";

@Component({
  components: { Datepicker },
})
class CartViewItem extends Vue {
  @Prop({ default: null })
  itemData!: CartItem;

  currentPriceInfo!: PriceInfo | null;
  currentPriceRules!: PriceInfo[];
  currentPrice!: number | undefined;
  totalPrice!: string;
  totalDiscount!: string;
  lineDiscountSum!: number;
  priceInfoBox!: string;
  moqInfoBox!: string;
  customQuantity!: number | undefined;
  currentDiscount!: number | undefined;
  originalQuantity!: number | undefined;
  artworkSelections: Record<string, SelectedArtWork> = {};
  ArtworkTypesconfig = artworkTypeConfig;

  artworkComment = "";
  showNotInRange = false;

  // artworkBox = { name: "", id: "" };
  artworkLabel = { name: "", id: "" };
  artworkLeaflet = { name: "", id: "" };
  artworkSachet = { name: "", id: "" };
  artworkShipper = { name: "", id: "" };

  selectedDate!: Date | undefined;
  dateFormat = moment()
    .localeData()
    .longDateFormat("L")
    .replace("YYYY", "yyyy")
    .replace("DD", "dd");

  cartStore = useCartStore();
  cartCount = 0;

  mounted() {
    this.artworkComment = this.itemData.comment;
    // this.artworkBox = this.itemData.artworkBox;
    this.artworkSelections = this.itemData.artWorkSelections;
    // this.artworkLabel = this.itemData.artworkLabel;
    // this.artworkLeaflet = this.itemData.artworkLeaflet;
    // this.artworkSachet = this.itemData.artworkSachet;
    // this.artworkShipper = this.itemData.artworkShipper;
    this.selectedDate = this.itemData.requestedDeliveryDate;
    if (
      this.itemData.product &&
      this.itemData.product.priceRules &&
      this.itemData.product.priceRules?.length > 0
    ) {
      this.currentPriceRules = this.itemData.product.priceRules;

      this.currentPriceInfo = findPriceRange(
        this.itemData.quantity,
        this.itemData.product.priceRules
      );

      if (this.currentPriceInfo) {
        this.calculatePrices(this.currentPriceInfo);
      }
      this.fillPriceBoxInfo(this.itemData.product);

      this.moqInfoBox = "Minimum order quantity";

      this.customQuantity = this.itemData.quantity;
      this.originalQuantity = this.itemData.quantity;
    }
  }

  fillPriceBoxInfo(productInfo: ProductInformation) {
    let priceTableHtml = "<table class='price-table'>";
    priceTableHtml +=
      "<tr><th align='right'>Order quantity</th><th align='right'>Price/box</th>";

    const hasMergedDiscounts =
      productInfo.priceRules?.some(
        (priceRule) => priceRule.discount != null && priceRule.discount > 0
      ) ?? false;

    if (hasMergedDiscounts) {
      priceTableHtml += "<th align='right'>Discount</th>";
    }
    priceTableHtml += "</tr>";

    if (
      productInfo &&
      productInfo.priceRules &&
      productInfo.priceRules.length > 0
    ) {
      productInfo.priceRules.map((productRule) => {
        const from = this.formatNumberWithSpaces(productRule.fromQuantity);
        const to = productRule.toQuantity
          ? this.formatNumberWithSpaces(productRule.toQuantity)
          : "";
        const currency = this.formatCurrency(
          productRule.price!,
          productInfo.currency
        );

        priceTableHtml +=
          "<tr><td align='right'>" +
          from +
          " - " +
          to +
          "</td><td align='right'>" +
          currency +
          "</td>";

        if (hasMergedDiscounts) {
          if (productRule.discount != null && productRule.discount > 0) {
            priceTableHtml +=
              "<td align='right'>" +
              this.formatNumber(productRule.discount) +
              "%</td>";
          }
        }
        priceTableHtml += "</tr>";
      });
    }
    priceTableHtml += "</table>";

    // SECTION 2: Separate discounts (when discounts[] is non-empty)
    if (productInfo.discounts && productInfo.discounts.length > 0) {
      let discountTableHtml = "<br/><table class='price-table'>";

      discountTableHtml +=
        "<tr><th align='right'>Order quantity</th><th align='right'>Discount</th></tr>";

      productInfo.discounts.map((disc) => {
        const from = this.formatNumberWithSpaces(disc.fromQuantity);
        const to = disc.toQuantity
          ? this.formatNumberWithSpaces(disc.toQuantity)
          : "";

        discountTableHtml +=
          "<tr><td align='right'>" +
          from +
          " - " +
          to +
          "</td><td align='right'>" +
          this.formatNumber(disc.discount!) +
          "%</td></tr>";
      });
      discountTableHtml += "</table>";

      priceTableHtml += discountTableHtml;
    }
    this.priceInfoBox = priceTableHtml;
  }

  updateCart() {
    this.cartStore.updateCartItem(
      this.itemData.product,
      this.customQuantity!,
      this.currentDiscount!,
      this.artworkSelections,
      this.artworkComment,
      this.selectedDate
    );

    const productName: string | undefined =
      this.itemData.product.customerProductName ?? this.itemData.product.name;
    toast.success("Product " + productName + " is updated", {
      position: "top-center",
    });
  }

  removeProduct() {
    this.cartStore.removeCartItem(this.itemData.product);
    this.currentDiscount = undefined;
    this.customQuantity = undefined;
  }

  getValue(param: any) {
    return param ? param : "";
  }

  formatNumber(num: number) {
    if (num) {
      return Number(num).toFixed(2);
    } else {
      return "";
    }
  }

  formatNumberWithSpaces(num: number | undefined): string {
    if (num) {
      return new Intl.NumberFormat("en-US", {
        useGrouping: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
        .format(num)
        .replace(/,/g, " ");
    } else {
      return "";
    }
  }

  // quantityChange() {
  //   this.currentPriceInfo = this.currentPriceRules.filter(
  //     (x) => x.fromQuantity === this.customQuantity
  //   )[0];
  //   this.calculatePrices(this.currentPriceInfo);
  // }

  customQuantityChange() {
    this.currentDiscount = undefined;
    this.showNotInRange = false;
    if (
      this.customQuantity !== undefined &&
      this.customQuantity >= this.itemData.product.moq!
    ) {
      // Find matching discount value
      const matchingQuantityItem = findPriceRange(
        this.customQuantity,
        this.currentPriceRules
      );

      if (matchingQuantityItem) {
        const effectiveDiscount = getEffectiveDiscountPercent(
          this.itemData.product,
          this.customQuantity,
          matchingQuantityItem
        );

        this.currentDiscount = effectiveDiscount;
        this.currentPrice = matchingQuantityItem.price;
        this.calculatePrices(matchingQuantityItem);
      } else {
        // Quantity is not within any range in currentPriceRules
        if (this.customQuantity && this.customQuantity > 0) {
          this.showNotInRange = true;
        }
      }
    }
  }

  calculatePrices(priceItem: PriceInfo) {
    if (!priceItem) return;

    const qty = this.customQuantity
      ? this.customQuantity
      : this.itemData.quantity;

    const discountPercent =
      this.currentDiscount != null
        ? this.currentDiscount
        : this.itemData.discount != null
        ? this.itemData.discount
        : priceItem.discount ?? 0;

    this.currentDiscount = discountPercent;
    const totalSum = priceItem.price! * qty!;
    this.lineDiscountSum = totalSum * (discountPercent / 100);

    this.totalDiscount = this.formatCurrency(
      this.lineDiscountSum,
      this.itemData.product.currency
    );

    this.totalPrice = this.formatCurrency(
      totalSum - this.lineDiscountSum,
      this.itemData.product.currency
    );
  }

  formatCurrency(value: number, currency: string | undefined = "USD"): string {
    return formatCurrency(value, currency);
  }
}
export default CartViewItem;
</script>
